import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import decodeJson from '@seccon/shared/utils/decodeJson';
import ApiError from '@seccon/shared/utils/ApiError';

import { FiltersContext } from '../contexts/filters';

/**
 * Fetches the list of unassigned personnel that can be assigned to this contract
 * @param {[queryId: string, contractId: number, filters: object]} context - react query context
 * @returns {Promise<object>} - the list of unassigned personnel
 */
function fetchUnassignedPersonnel (context) {
  // eslint-disable-next-line no-unused-vars
  const [_, contractId, filters] = context.queryKey;

  const requestBody = {};

  // If filters have been applied to the view, add them to the request body
  if (filters) {
    if (filters.eligibility) {
      requestBody.eligibility = filters.eligibility.map((entry) => entry.value);
    }

    if (filters.specialAccesses) {
      requestBody.specialAccesses = filters.specialAccesses.map((entry) => entry.value);
    }

    if (filters.name) {
      requestBody.name = filters.name;
    }

    if (filters.facilityIds) {
      requestBody.facilityIds = filters.facilityIds.map((entry) => entry.value);
    }
  }

  return fetch(`/contractAssignment/contract/${ contractId }/listUnassignedPersonnel`, { method: 'POST', body: JSON.stringify(requestBody) })
    .then(decodeJson)
    .catch(({ body, status }) => {
      if (status === 404) {
        throw new ApiError('Unable to locate contract for personnel assignment. Reselect it from the contract list and choose "Assign Personnel".');
      }
      throw new ApiError(body ? `Error: ${ body }` : 'Unable to retrieve personnel for contract assignment. Please reload the page and try again');
    });
}

/**
 * Processes the unassigned personnel API response into a field array of personnel info, dropdown options, and field settings.
 * @param {object} response - API response
 * @returns {object[]} - field array of personnel data
 */
function processUnassignedPersonnel (response) {
  let newList = [];

  // Without this, errors fail silently in react-query's select callback
  try {
    newList = response.unassignedPersonnel.map((person) => {
      // Process the clearance levels and create a list of eligibility options
      const eligibilityOptions = person.clearanceLevels.filter((clearance) => !!clearance.eligibility)
        .map((level) => {
          let label = level.eligibility;

          // If the granting agency is indicated, add that to the eligibility label
          if (level.grantingAgency) {
            label += ` (${ level.grantingAgency })`;
          }

          return {
            label,
            value: level.clearanceId
          };
        });

      // Process the special accesses into a list of special access options
      const specialAccessOptions = person.mapSpecialAccesses
        .sort((accessA, accessB) => (new Date(accessB?.dateCreated) - new Date(accessA?.dateCreated)))
        .map((access) => ({
          label: access.name,
          value: access.id
        }));

      return {
        // Access required field value will be initialized once the access required options are fetched
        accessRequired: null,
        companyOrDbaNameAndFacilityCode: person.companyOrDbaNameAndFacilityCode,
        // Set eligibility to empty
        eligibility: null,
        // Set dateAssigned to empty
        dateAssigned: null,
        eligibilityOptions,
        facilityId: person.facilityId,
        firstName: person.firstName,
        lastName: person.lastName,
        name: person.name,
        personnelId: person.id,
        // Set special access field to an empty array
        specialAccesses: [],
        specialAccessOptions,
        ssn: person.ssn,
        // Set status to empty
        status: null
      }
    }).sort((personA, personB) => personA.name.localeCompare(personB.name, 'en', { sensitivity: 'base' }));
  } catch (err) {
    console.error(err);
  }

  return newList;
}

const useUnassignedPersonnel = () => {
  /* Personnel list from server*/
  const { contractId } = useParams();
  const filters = React.useContext(FiltersContext);

  const { data, isError, isLoading } = useQuery({
    queryKey: ['useUnassignedPersonnel', contractId, filters],
    queryFn: fetchUnassignedPersonnel,
    select: processUnassignedPersonnel,
    staleTime: Infinity
  });

  return { data, isError, isLoading };
}

export default useUnassignedPersonnel;
